import CreateExternalLinkModal from "./CreateExternalLinkModal";
import ManageExternalLinksModal from "./ManageExternalLinksModal";
import ExternalLinkStatsModal from "./ExternalLinkStatsModal";
import RevokeExternalLinkModal from "./RevokeExternalLinkModal";
import type { UseExternalLinksResult } from "./useExternalLinks";

/**
 * Renders whichever external-link dialog the flow is currently on.
 *
 * A single host component so a call site only mounts one thing: the File Manager, the details panel
 * and any future surface all render `<ExternalLinksHost state={useExternalLinks()} />` rather than
 * repeating four conditional blocks and re-deriving the transitions between them.
 */
type ExternalLinksHostProps = {
  state: UseExternalLinksResult;
};

function ExternalLinksHost({ state }: Readonly<ExternalLinksHostProps>) {
  const {
    dialog,
    target,
    activeLink,
    refreshToken,
    openCreate,
    openEdit,
    openStats,
    openRevoke,
    closeAll,
    handleMutated,
    backToManage,
  } = state;

  if (!target || dialog === "none") return null;

  if (dialog === "create" || dialog === "edit") {
    const isEdit = dialog === "edit";
    return (
      <CreateExternalLinkModal
        // Remounts between create and edit, and between different links: the form seeds its state
        // once from props, so reusing the instance would keep showing the previous link's values.
        key={isEdit ? `edit-${activeLink?._id}` : "create"}
        target={target}
        editingLink={isEdit ? activeLink : null}
        onClose={isEdit ? backToManage : closeAll}
        onSaved={handleMutated}
        onManage={() => backToManage()}
      />
    );
  }

  if (dialog === "manage") {
    return (
      <ManageExternalLinksModal
        target={target}
        refreshToken={refreshToken}
        onClose={closeAll}
        onCreate={() => openCreate(target)}
        onEdit={openEdit}
        onStats={openStats}
        onRevoke={openRevoke}
        onMutated={handleMutated}
        // A link whose expiry has already passed cannot be reinstated as-is (PRD 3.4), so Enable Link
        // hands off to Edit: the creator sets a new date there and saving both re-dates and revives
        // the link, instead of a second dialog that only asks for a date.
        onReinstateNeedsExpiry={openEdit}
      />
    );
  }

  if (dialog === "stats" && activeLink) {
    return <ExternalLinkStatsModal link={activeLink} onClose={backToManage} />;
  }

  if (dialog === "revoke" && activeLink) {
    return (
      <RevokeExternalLinkModal
        link={activeLink}
        onClose={backToManage}
        onRevoked={() => {
          handleMutated();
          backToManage();
        }}
      />
    );
  }

  return null;
}

export default ExternalLinksHost;
